import { availableMetric, pendingMetric, type MetricValue } from '@fleetcore/shared'
import type { LiveVehicleState } from '@fleetcore/shared/telemetry'

import type {
  CapabilityProfile,
  CapabilityStatus,
  ChannelAvailability,
  ChannelKey,
} from './api-types'

/**
 * Which of the capability answers applies to ONE channel, and therefore which
 * sentence a gauge shows in place of a number.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  THE FAILURE THIS FILE EXISTS TO PREVENT IS A NEEDLE AT ZERO.
 * ─────────────────────────────────────────────────────────────────────────────
 * A needle resting on 0 rpm is a claim that the engine is off, and a fleet manager
 * reads it as one. A car without a CAN adapter has no rpm to report, and the honest
 * rendering of that is a dial with no needle and a sentence naming the program number
 * to check — never a zero, never a blank card. Every branch below produces either a
 * measured value or an explained absence; there is no third outcome.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  FOUR ANSWERS, NOT TWO.
 * ─────────────────────────────────────────────────────────────────────────────
 *  reporting          a value arrived, render it
 *  never_reported     this vehicle has never sent this channel — wrong program
 *                     number, wrong adapter, or no adapter at all
 *  stopped_reporting  it used to, and we can name the date it stopped — a harness,
 *                     a fuse or a battery disconnect, which is a different phone call
 *  unknown            we have no capability evidence yet (the profile has not loaded,
 *                     or the API predates the projection). Saying "never reported"
 *                     here would be a diagnosis we have not earned.
 *
 * The vocabulary is `@fleetcore/shared/telemetry`'s, deliberately: the API derives the
 * profile with `deriveCapabilityProfile()` and the UI must not invent a fifth word for
 * the same fact.
 */

/** `CapabilityStatus` plus the honest "we have not been told" that a UI needs. */
export type CapabilityKnowledge = CapabilityStatus | 'unknown'

/**
 * The availability class of every channel this app puts on a gauge.
 *
 * It is duplicated from the shared registry rather than imported from it, and the
 * duplication is deliberate: `@fleetcore/shared/telemetry` is a 391-channel registry
 * plus two codecs, and importing a value from it to learn one enum would put all of
 * that in the browser bundle for the sake of seventeen strings. `capability-model.test.ts`
 * imports the real registry — tests are not bundled — and fails if any entry here
 * disagrees with it, so the copy cannot drift.
 */
export const GAUGE_CHANNEL_AVAILABILITY = {
  speed_gps: 'always',
  vehicle_speed_can: 'can',
  engine_rpm: 'can',
  engine_load: 'can',
  accelerator_pedal: 'can',
  coolant_temperature: 'can',
  engine_temperature: 'can',
  fuel_level_pct: 'can',
  fuel_rate: 'can',
  total_mileage_can: 'can',
  engine_worktime: 'can',
  program_number: 'can',
  obd_dtc_count: 'can',
  external_voltage: 'always',
  battery_voltage: 'always',
  control_module_voltage: 'can',
  gsm_signal: 'always',
} as const satisfies Record<ChannelKey, ChannelAvailability>

export type GaugeChannel = keyof typeof GAUGE_CHANNEL_AVAILABILITY

export function availabilityOf(channel: ChannelKey): ChannelAvailability | null {
  return GAUGE_CHANNEL_AVAILABILITY[channel as GaugeChannel] ?? null
}

export interface ChannelStatus {
  channel: ChannelKey
  status: CapabilityKnowledge
  /** ISO 8601. The date a "stopped reporting" message must name; null otherwise. */
  lastSeenAt: string | null
  availability: ChannelAvailability | null
  /**
   * True when the bus is alive but this particular channel is not in it — the
   * program number selects a SUBSET of the CAN channels, so "the adapter works and
   * this make/model does not publish rpm" is a real and different answer from
   * "there is no adapter".
   */
  outsideProgram: boolean
}

/**
 * What the platform knows about one channel on one vehicle.
 *
 * Reads the per-channel projection when the API offers it, and falls back to the CAN
 * verdict for CAN channels when it does not: a bus that has never reported cannot have
 * reported rpm, and that inference is sound without the projection. Nothing is inferred
 * in the other direction — a live bus tells us nothing about whether THIS channel was
 * ever in the program, so that case answers `outsideProgram` rather than a status.
 */
export function channelStatus(
  profile: CapabilityProfile | undefined,
  channel: ChannelKey,
  availability = availabilityOf(channel),
): ChannelStatus {
  const base = { channel, availability, outsideProgram: false }

  if (profile === undefined) return { ...base, status: 'unknown', lastSeenAt: null }

  const observed = profile.channels?.find((entry) => entry.channel === channel)
  if (observed !== undefined) {
    return {
      ...base,
      availability: observed.availability ?? availability,
      status: observed.status,
      lastSeenAt: observed.lastSeenAt,
    }
  }

  if (availability !== 'can') return { ...base, status: 'unknown', lastSeenAt: null }

  // `available: false` is the Phase 2 shape's only way of saying "no CAN". It cannot
  // distinguish never from stopped, so it degrades to `never_reported` — which is what
  // the Phase 2 UI already claimed, rather than a new and stronger claim.
  const canStatus =
    profile.canBus.status ?? (profile.canBus.available ? 'reporting' : 'never_reported')

  if (canStatus === 'stopped_reporting') {
    return { ...base, status: 'stopped_reporting', lastSeenAt: profile.canBus.lastSeenAt ?? null }
  }
  if (canStatus === 'never_reported') return { ...base, status: 'never_reported', lastSeenAt: null }
  if (canStatus === 'reporting') {
    // The projection would have listed this channel if it had ever arrived. Without
    // the projection all we can say is that the bus works and this value does not
    // come off it — which is the program number's doing, not a fault.
    return {
      ...base,
      status: profile.channels === undefined ? 'unknown' : 'never_reported',
      lastSeenAt: null,
      outsideProgram: true,
    }
  }

  return { ...base, status: 'unknown', lastSeenAt: null }
}

/** i18n keys for the sentence that replaces a number. Resolved by the caller. */
export const CHANNEL_REASON_KEY = {
  never_can: 'vehicles:empty.noCanData.title',
  never: 'vehicles:capability.channel.neverReported',
  stopped: 'vehicles:capability.channel.stoppedReporting',
  outside_program: 'vehicles:capability.channel.outsideProgram',
  unknown: 'common:metric.pendingTelemetry',
} as const

export interface ChannelReading {
  channel: ChannelKey
  metric: MetricValue<number>
  status: CapabilityKnowledge
  lastSeenAt: string | null
  availability: ChannelAvailability | null
  outsideProgram: boolean
  /**
   * The catalog key for the absence. Carried alongside the metric because a
   * "stopped on 14 March" sentence needs the date interpolated into it, and
   * `PendingMetric.reasonKey` is a bare key with nowhere to put one.
   */
  reasonKey: string | null
}

export interface ChannelReadingOptions {
  /**
   * Overrides the sentence when the device is present and this channel simply did
   * not arrive — "Tension non remontée par le boîtier" reads better on a voltage
   * than the generic wording, and it is still an explained absence.
   */
  silentReasonKey?: string
}

/**
 * A channel's live value, or the explained absence that must be drawn instead.
 *
 * The live projection is the source for the value: it holds the newest sample per
 * channel, so a channel missing from it has genuinely not arrived. The capability
 * profile is the source for the REASON, which is the half that turns a blank dial
 * into a work order.
 */
export function capabilityReading(
  state: LiveVehicleState | null,
  profile: CapabilityProfile | undefined,
  channel: ChannelKey,
  options: ChannelReadingOptions = {},
): ChannelReading {
  const status = channelStatus(profile, channel)
  const value = state?.channels?.[channel] ?? null

  if (value !== null) {
    return {
      ...status,
      metric: availableMetric(value),
      reasonKey: null,
    }
  }

  const reasonKey = absenceReasonKey(status, state, options)
  return {
    ...status,
    // `not_supported_by_vehicle` for a capability verdict we have earned,
    // `pending_telemetry` for one we have not. The two render differently and a
    // support agent acts on them differently.
    metric:
      status.status === 'unknown'
        ? pendingMetric('pending_telemetry', { reasonKey })
        : pendingMetric('not_supported_by_vehicle', { reasonKey }),
    reasonKey,
  }
}

function absenceReasonKey(
  status: ChannelStatus,
  state: LiveVehicleState | null,
  options: ChannelReadingOptions,
): string {
  if (status.status === 'stopped_reporting') return CHANNEL_REASON_KEY.stopped
  if (status.outsideProgram && status.status !== 'unknown') {
    return CHANNEL_REASON_KEY.outside_program
  }
  if (status.status === 'never_reported') {
    return status.availability === 'can' ? CHANNEL_REASON_KEY.never_can : CHANNEL_REASON_KEY.never
  }
  // Unknown. A CAN channel on a vehicle whose profile has not loaded still names the
  // bus, because that is the likely cause and the message is a hypothesis either way.
  if (status.availability === 'can') return CHANNEL_REASON_KEY.never_can
  if (options.silentReasonKey !== undefined && state !== null) return options.silentReasonKey
  return CHANNEL_REASON_KEY.unknown
}

/**
 * The CAN bus verdict for the whole vehicle, in the same three states.
 *
 * Falls back to the Phase 2 boolean when the projection is absent. `available: false`
 * cannot distinguish "never" from "stopped", so the fallback answers `never_reported`
 * — which is what the Phase 2 UI already said, rather than a new claim.
 */
export interface CanBusVerdict {
  status: CapabilityKnowledge
  reason: string
  lastSeenAt: string | null
  programNumber: number | null
  reportingChannels: number
}

export function canBusVerdict(profile: CapabilityProfile | undefined): CanBusVerdict {
  if (profile === undefined) {
    return {
      status: 'unknown',
      reason: 'never_reported',
      lastSeenAt: null,
      programNumber: null,
      reportingChannels: 0,
    }
  }

  const can = profile.canBus
  const status: CapabilityStatus = can.status ?? (can.available ? 'reporting' : 'never_reported')
  return {
    status,
    reason: can.reason ?? defaultReason(status, can.programNumber ?? null),
    lastSeenAt: can.lastSeenAt ?? null,
    programNumber: can.programNumber,
    reportingChannels: can.reportedChannels,
  }
}

/** The same fork `canBusReason()` makes in shared, for an API that has not sent one. */
function defaultReason(status: CapabilityStatus, programNumber: number | null): string {
  if (status === 'reporting') return 'reporting'
  if (status === 'stopped_reporting') return 'stopped_reporting'
  if (programNumber === null) return 'never_reported_program_unknown'
  if (programNumber === 0) return 'never_reported_program_unset'
  return 'never_reported'
}
